import * as mutation from "./mutation-types";
import * as action from "./action-types";
import * as systemLogMutation from "@/store/system-logs/mutation-types";
import * as lcpMutation from "@/store/levy-collection-providers/mutation-types";
import * as levyEstimatorMutation from "@/store/levy-estimator/mutation-types";
import * as invoiceMutation from "@/store/invoices/mutation-types";
import * as noticeMutation from "@/store/notices/mutation-types";
import * as dashboardMutation from "@/store/dashboard/mutation-types";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";
import { GLOBAL_ERROR } from "@/store/system/mutation-types";
import { LOAD_DYNAMIC_LOOKUPS } from "@/store/lookups/action-types";
import { tableHelper } from "@/helpers/table.helper";
import { deepCopyAxiosException } from "@/store/helpers";
import axios from "axios";
import _ from "underscore";

export const actions = {
  async [action.LIST]({ commit, state }) {
    commit(mutation.LIST_STARTED);
    try {
      const url = tableHelper.constructUrl("/api/levy-collections", state.listing.paging, state.listing.criteria);
      const { headers, data } = await axios.get(url);
      commit(mutation.LIST_SET_PAGING, tableHelper.getPagingFromHeaders(headers));
      commit(mutation.LIST_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.LIST_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.LIST_NEXT_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page + 1 });
    await dispatch(action.LIST);
  },
  async [action.LIST_PREVIOUS_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page - 1 });
    await dispatch(action.LIST);
  },

  async [action.VIEW]({ commit }, id) {
    commit(mutation.VIEW_STARTED);
    try {
      const url = `/api/levy-collections/${id}`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.VIEW_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.VIEW_REFRESH]({ state, dispatch }, levyCollectionId) {
    const needsRefreshed = (!state.view.loaded && !state.view.loading) || state.view.data.id != levyCollectionId;
    if (needsRefreshed) {
      await dispatch(action.VIEW, levyCollectionId);
    }
  },

  async [action.VIEW_INVOICING_TIMING_CONFIGURATION]({ commit, dispatch }, levyCollectionId) {
    commit(mutation.VIEW_INVOICING_TIMING_CONFIGURATION_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/invoicing-timing-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_INVOICING_TIMING_CONFIGURATION_SUCCEEDED, data);
      await dispatch(LOAD_DYNAMIC_LOOKUPS);
      return true;
    } catch (e) {
      commit(mutation.VIEW_INVOICING_TIMING_CONFIGURATION_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.EDIT_INVOICING_TIMING_CONFIGURATION_GET]({ commit }, levyCollectionId) {
    commit(mutation.EDIT_INVOICING_TIMING_CONFIGURATION_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/invoicing-timing-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_INVOICING_TIMING_CONFIGURATION_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_INVOICING_TIMING_CONFIGURATION_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_INVOICING_TIMING_CONFIGURATION_SAVE]({ commit, state }) {
    commit(mutation.EDIT_INVOICING_TIMING_CONFIGURATION_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${
        state.editInvoicingTimingConfiguration.levyCollectionId
      }/invoicing-timing-configuration`;
      const { data } = await axios.put(url, state.editInvoicingTimingConfiguration.data);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(mutation.EDIT_INVOICING_TIMING_CONFIGURATION_SAVE_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, "Invoicing timing updated.");
      return true;
    } catch (e) {
      commit(mutation.EDIT_INVOICING_TIMING_CONFIGURATION_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW_LEVY_CALCULATION_TIMING_CONFIGURATION]({ commit }, levyCollectionId) {
    commit(mutation.VIEW_LEVY_CALCULATION_TIMING_CONFIGURATION_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/levy-calculation-timing-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_LEVY_CALCULATION_TIMING_CONFIGURATION_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.VIEW_LEVY_CALCULATION_TIMING_CONFIGURATION_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_GET]({ commit }, levyCollectionId) {
    commit(mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/levy-calculation-timing-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_SAVE]({ commit, state }) {
    commit(mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${
        state.editLevyCalculationTimingConfiguration.levyCollectionId
      }/levy-calculation-timing-configuration`;
      const { data } = await axios.put(url, state.editLevyCalculationTimingConfiguration.data);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_SAVE_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, "Levy calculation timing updated.");
      return true;
    } catch (e) {
      commit(mutation.EDIT_LEVY_CALCULATION_TIMING_CONFIGURATION_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_GET]({ commit }, levyCollectionId) {
    commit(mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/public-levy-estimator-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_SAVE]({ commit, state }) {
    commit(mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${
        state.editPublicLevyEstimatorConfiguration.levyCollectionId
      }/public-levy-estimator-configuration`;
      const { data } = await axios.put(url, state.editPublicLevyEstimatorConfiguration.data);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_SAVE_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, "Public levy estimator configuration updated.");
      return true;
    } catch (e) {
      commit(mutation.EDIT_PUBLIC_LEVY_ESTIMATOR_CONFIGURATION_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW_INVOICE_CONFIGURATION]({ commit }, levyCollectionId) {
    commit(mutation.VIEW_INVOICE_CONFIGURATION_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/invoice-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_INVOICE_CONFIGURATION_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.VIEW_INVOICE_CONFIGURATION_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.EDIT_INVOICE_CONFIGURATION_GET]({ commit }, levyCollectionId) {
    commit(mutation.EDIT_INVOICE_CONFIGURATION_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/invoice-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_INVOICE_CONFIGURATION_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_INVOICE_CONFIGURATION_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_INVOICE_CONFIGURATION_SAVE]({ commit, state }) {
    commit(mutation.EDIT_INVOICE_CONFIGURATION_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${state.editInvoiceConfiguration.levyCollectionId}/invoice-configuration`;
      const { data } = await axios.put(url, state.editInvoiceConfiguration.data);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(mutation.EDIT_INVOICE_CONFIGURATION_SAVE_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, "Levy collection invoice configuration updated.");
      return true;
    } catch (e) {
      commit(mutation.EDIT_INVOICE_CONFIGURATION_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW_EMAIL_CONFIGURATION]({ commit }, levyCollectionId) {
    commit(mutation.VIEW_EMAIL_CONFIGURATION_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/email-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_EMAIL_CONFIGURATION_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.VIEW_EMAIL_CONFIGURATION_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_EMAIL_CONFIGURATION_GET]({ commit }, levyCollectionId) {
    commit(mutation.EDIT_EMAIL_CONFIGURATION_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/email-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_EMAIL_CONFIGURATION_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_EMAIL_CONFIGURATION_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_EMAIL_CONFIGURATION_SAVE]({ commit, state }) {
    commit(mutation.EDIT_EMAIL_CONFIGURATION_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${state.editEmailConfiguration.levyCollectionId}/email-configuration`;
      const { data } = await axios.put(url, state.editEmailConfiguration.data);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(mutation.EDIT_EMAIL_CONFIGURATION_SAVE_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, "Levy collection email configuration updated.");
      return true;
    } catch (e) {
      commit(mutation.EDIT_EMAIL_CONFIGURATION_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW_NON_COMPLIANCE_HISTORY_CONFIGURATION]({ commit }, levyCollectionId) {
    commit(mutation.VIEW_NON_COMPLIANCE_HISTORY_CONFIGURATION_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/non-compliance-history-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_NON_COMPLIANCE_HISTORY_CONFIGURATION_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.VIEW_NON_COMPLIANCE_HISTORY_CONFIGURATION_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_GET]({ commit }, levyCollectionId) {
    commit(mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/non-compliance-history-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SAVE]({ commit, state }) {
    commit(mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${
        state.editNonComplianceHistoryConfiguration.levyCollectionId
      }/non-compliance-history-configuration`;
      const { data } = await axios.put(url, state.editNonComplianceHistoryConfiguration.data);
      commit(mutation.STATE_UNLOAD);
      commit(lcpMutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SAVE_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, "Non-compliance history configuration updated.");
      return true;
    } catch (e) {
      commit(mutation.EDIT_NON_COMPLIANCE_HISTORY_CONFIGURATION_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW_DATA_SOURCE_CONFIGURATION]({ commit }, levyCollectionId) {
    commit(mutation.VIEW_DATA_SOURCE_CONFIGURATION_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/data-source-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_DATA_SOURCE_CONFIGURATION_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.VIEW_DATA_SOURCE_CONFIGURATION_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_DATA_SOURCE_CONFIGURATION_GET]({ commit }, levyCollectionId) {
    commit(mutation.EDIT_DATA_SOURCE_CONFIGURATION_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/data-source-configuration`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_DATA_SOURCE_CONFIGURATION_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_DATA_SOURCE_CONFIGURATION_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_DATA_SOURCE_CONFIGURATION_SAVE]({ commit, state }) {
    commit(mutation.EDIT_DATA_SOURCE_CONFIGURATION_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${
        state.editDataSourceConfiguration.levyCollectionId
      }/data-source-configuration`;
      const { data } = await axios.put(url, state.editDataSourceConfiguration.data);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(mutation.EDIT_DATA_SOURCE_CONFIGURATION_SAVE_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, "Levy collection data source configuration updated.");
      return true;
    } catch (e) {
      commit(mutation.EDIT_DATA_SOURCE_CONFIGURATION_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW_FEES]({ commit }, levyCollectionId) {
    commit(mutation.VIEW_FEES_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/fees`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_FEES_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.VIEW_FEES_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_FEES_GET]({ commit }, id) {
    commit(mutation.EDIT_FEES_GET_STARTED);
    try {
      const url = `/api/levy-collections/${id}`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_FEES_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_FEES_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_FEES_SAVE]({ commit, state }) {
    commit(mutation.EDIT_FEES_SAVE_STARTED);
    try {
      const url = `/api/levy-collections/${state.editFees.data.id}/fees`;
      await axios.put(url, state.editFees.data);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(dashboardMutation.STATE_UNLOAD);
      commit(lcpMutation.STATE_UNLOAD);
      commit(levyEstimatorMutation.STATE_UNLOAD);
      commit(mutation.EDIT_FEES_SAVE_SUCCEEDED);
      commit(SHOW_NOTIFICATION, "Levy collection fees & rates updated.");
      return true;
    } catch (e) {
      commit(mutation.EDIT_FEES_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW_INFORMATION_SESSIONS]({ commit }, levyCollectionId) {
    commit(mutation.VIEW_INFORMATION_SESSIONS_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/information-sessions`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_INFORMATION_SESSIONS_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.VIEW_INFORMATION_SESSIONS_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_INFORMATION_SESSION_GET]({ commit }, { levyCollectionId, informationSessionId }) {
    commit(mutation.EDIT_INFORMATION_SESSION_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/information-sessions/${informationSessionId}`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_INFORMATION_SESSION_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_INFORMATION_SESSION_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_INFORMATION_SESSION_SAVE]({ commit, state }) {
    commit(mutation.EDIT_INFORMATION_SESSION_SAVE_STARTED);
    try {
      const levyCollectionId = state.editInformationSession.levyCollectionId;
      const url = `/api/levy-collections/${levyCollectionId}/information-sessions`;
      const data = state.editInformationSession.data;
      if (data.id) {
        await axios.put(`${url}/${data.id}`, data);
      } else {
        await axios.post(url, data);
      }
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(lcpMutation.STATE_UNLOAD);
      commit(mutation.EDIT_INFORMATION_SESSION_SAVE_SUCCEEDED);
      const message = data.id ? "Information session details updated." : "Information session created.";
      commit(SHOW_NOTIFICATION, message);
      return true;
    } catch (e) {
      commit(mutation.EDIT_INFORMATION_SESSION_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_INFORMATION_SESSION_DELETE]({ commit }, { levyCollectionId, informationSessionId }) {
    commit(mutation.EDIT_INFORMATION_SESSION_DELETE_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/information-sessions/${informationSessionId}`;
      await axios.delete(url);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(lcpMutation.STATE_UNLOAD);
      commit(mutation.EDIT_INFORMATION_SESSION_DELETE_SUCCEEDED);
      commit(SHOW_NOTIFICATION, "Information session cancelled.");
      return true;
    } catch (e) {
      commit(mutation.EDIT_INFORMATION_SESSION_DELETE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.GET_PROVIDER_TASKS]({ commit }, levyCollectionId) {
    commit(mutation.GET_PROVIDER_TASKS_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/provider-tasks`;
      const { data } = await axios.get(url);
      commit(mutation.GET_PROVIDER_TASKS_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.GET_PROVIDER_TASKS_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.TASKS_GET]({ commit }, levyCollectionId) {
    commit(mutation.TASKS_GET_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/tasks`;
      const { data } = await axios.get(url);
      commit(mutation.TASKS_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.TASKS_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.TASK_RUN]({ commit, dispatch }, { type, levyCollectionId, description }) {
    commit(mutation.TASK_RUN_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/run-task`;
      await axios.post(url, { type });
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      commit(dashboardMutation.STATE_UNLOAD);
      commit(lcpMutation.STATE_UNLOAD);
      commit(levyEstimatorMutation.STATE_UNLOAD);
      commit(noticeMutation.STATE_UNLOAD);
      commit(invoiceMutation.STATE_UNLOAD);
      if (type == "Delete") {
        await dispatch(LOAD_DYNAMIC_LOOKUPS);
        commit(mutation.VIEW_CLEAR);
      }
      commit(mutation.TASK_RUN_SUCCEEDED);
      commit(SHOW_NOTIFICATION, `${description} completed successfully.`);
      return true;
    } catch (e) {
      commit(mutation.TASK_RUN_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.CREATE]({ commit, state, dispatch }) {
    commit(mutation.CREATE_STARTED);
    try {
      let { data } = await axios.post("/api/levy-collections", { type: state.create.type });
      await dispatch(LOAD_DYNAMIC_LOOKUPS);
      commit(mutation.STATE_UNLOAD);
      commit(mutation.CREATE_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, "Levy collection created.");
      return true;
    } catch (e) {
      commit(mutation.CREATE_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.GET_COMPONENT_LEVY_AMOUNTS](context, id) {
    context.commit(mutation.GET_COMPONENT_LEVY_AMOUNTS_STARTED);
    try {
      const { data } = await axios.get(`/api/levy-collections/${id}/statistics/provider-component-amounts`);
      const chartData = {
        labels: _.map(data, d => d.componentName),
        datasets: [{ data: _.map(data, d => d.total) }]
      };
      context.commit(mutation.GET_COMPONENT_LEVY_AMOUNTS_SUCCEEDED, chartData);
      return true;
    } catch (e) {
      context.commit(mutation.GET_COMPONENT_LEVY_AMOUNTS_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.INVOICE_TEMPLATE_UPLOAD]({ commit }, { levyCollectionId, formData }) {
    commit(mutation.INVOICE_TEMPLATE_UPLOAD_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/invoice-template`;
      const { data } = await axios.post(url, formData);
      commit(mutation.INVOICE_TEMPLATE_UPLOAD_SUCCEEDED, data);
      commit(SHOW_NOTIFICATION, "Invoice template was successfully uploaded.");
      commit(systemLogMutation.STATE_UNLOAD);
      return true;
    } catch (e) {
      commit(mutation.INVOICE_TEMPLATE_UPLOAD_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.FINISH_SETUP]({ commit, dispatch }, levyCollectionId) {
    commit(mutation.FINISH_SETUP_STARTED);
    try {
      await axios.put(`/api/levy-collections/${levyCollectionId}/setup`);
      commit(mutation.STATE_UNLOAD);
      commit(systemLogMutation.STATE_UNLOAD);
      await dispatch(LOAD_DYNAMIC_LOOKUPS);
      commit(mutation.FINISH_SETUP_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.FINISH_SETUP_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.GET_STATUS_HISTORY]({ commit }, levyCollectionId) {
    commit(mutation.GET_STATUS_HISTORY_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/status-history`;
      const { data } = await axios.get(url);
      commit(mutation.GET_STATUS_HISTORY_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.GET_STATUS_HISTORY_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  }
};
